import { clsx } from 'clsx'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface PopoverProps {
  trigger: (p: { open: boolean; toggle: () => void; ref: (el: HTMLElement | null) => void }) => ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
  align?: 'start' | 'center' | 'end'
  side?: 'bottom' | 'top'
  className?: string
}

/** Lightweight anchored popover rendered in a portal; closes on outside click and Escape. */
export function Popover({ trigger, children, align = 'start', side = 'bottom', className }: PopoverProps) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number; origin: string } | null>(null)
  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((o) => !o), [])

  const place = useCallback(() => {
    const a = anchor.current?.getBoundingClientRect()
    const p = panel.current?.getBoundingClientRect()
    if (!a || !p) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    let left =
      align === 'start' ? a.left : align === 'end' ? a.right - p.width : a.left + a.width / 2 - p.width / 2
    left = Math.max(8, Math.min(vw - p.width - 8, left))
    let top = side === 'bottom' ? a.bottom + 8 : a.top - p.height - 8
    if (side === 'bottom' && top + p.height > vh - 8) top = Math.max(8, a.top - p.height - 8)
    if (side === 'top' && top < 8) top = a.bottom + 8
    setPos({ left, top, origin: top > a.top ? 'top' : 'bottom' })
  }, [align, side])

  useLayoutEffect(() => {
    if (open) place()
    else setPos(null)
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
      }
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  return (
    <>
      {trigger({
        open,
        toggle,
        ref: (el) => {
          anchor.current = el
        },
      })}
      {open &&
        createPortal(
          <div
            ref={panel}
            role="dialog"
            style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, transformOrigin: pos?.origin }}
            className={clsx(
              'fixed z-[80] animate-pop rounded-2xl border border-line bg-surface p-1.5 text-fg shadow-lg',
              className,
            )}
          >
            {typeof children === 'function' ? children(close) : children}
          </div>,
          document.body,
        )}
    </>
  )
}

interface MenuItemProps {
  icon?: ReactNode
  label: string
  hint?: string
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}

export function MenuItem({ icon, label, hint, danger, disabled, onSelect }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onSelect}
      className={clsx(
        'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm transition-colors disabled:opacity-40',
        danger ? 'text-danger hover:bg-danger/10' : 'hover:bg-surface-3',
      )}
    >
      <span className="flex size-5 items-center justify-center text-fg-2 [&>svg]:size-[18px]">{icon}</span>
      <span className="flex-1">{label}</span>
      {hint && <span className="text-xs text-fg-3">{hint}</span>}
    </button>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 pt-2 pb-1 font-semibold text-[11px] text-fg-3 uppercase tracking-wider">
      {children}
    </div>
  )
}
